import React, { useEffect } from 'react';
import { Textarea } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';

interface NoteContentProps {
  note: string;
  isEditing: boolean;
  onNoteChange: (value: string) => void;
  onNoteFocus: () => void;
  onNoteBlur: () => void;
  setShowButtons: (value: boolean) => void;
  setIsEditing: (value: boolean) => void;
  isOverLimit: boolean;
  setIsOverLimit: (value: boolean) => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
}

const Content = ({
  note,
  isEditing,
  onNoteChange,
  onNoteFocus,
  onNoteBlur,
  setShowButtons,
  setIsEditing,
  isOverLimit,
  setIsOverLimit,
  textAreaRef,
}: NoteContentProps) => {
  // Calculate the number of rows based on content
  const numberOfRows = note.split('\n').length;

  const txtArea = {
    border: `1px solid ${Colors.ArmyGreen300}`,
    _active: {
      border: `1px solid ${
        isOverLimit ? Colors.SystemColorNegative : Colors.ArmyGreen300
      }`,
    },
    _focus: {
      border: `1px solid ${
        isOverLimit ? Colors.SystemColorNegative : Colors.ArmyGreen300
      }`,
    },
    paddingTop: 'padding_4',
  };

  const noteTxt = {
    whiteSpace: 'pre-line',
    wordBreak: 'break-all',
  };

  const handleTextChange = (value: string) => {
    if (value.length > 10000) {
      setIsOverLimit(true);
    } else {
      setIsOverLimit(false);
    }
    onNoteChange(value);
    setShowButtons(true);
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing) {
      // Check if the textarea is available in the DOM
      if (textAreaRef.current) {
        // Focus on the textarea
        textAreaRef.current.focus();
      }
    }
  }, [isEditing, textAreaRef.current]);

  if (isEditing || !note) {
    return (
      <>
        <Textarea
          key="note"
          ref={textAreaRef}
          placeholder="Add a note..."
          value={note}
          onChange={(e) => handleTextChange(e.target.value)}
          onFocus={onNoteFocus}
          onBlur={onNoteBlur}
          readOnly={Boolean(note) && !isEditing}
          rows={numberOfRows} // Set the number of rows
          resize="vertical"
          sx={txtArea}
        />
        {isOverLimit && (
          <Text.Small color={Colors.SystemColorNegative}>
            Limit 10,000 characters
          </Text.Small>
        )}
      </>
    );
  }

  return (
    <Text sx={noteTxt} as="p">
      {note}
    </Text>
  );
};

export default Content;
